/**
 * CRM v5 · Vista Calendario Prenotazioni — riscrittura Agenda v2.
 *
 * Cambi rispetto alla versione precedente:
 * - Vista Giorno: UNA SOLA colonna prenotazioni (rimossa la split per zona).
 *   Le zone/tavoli restano sulla card. Altezza slot DINAMICA: cresce con
 *   il numero di prenotazioni, nessuna sovrapposizione né limite fittizio.
 * - Vista Settimana: 7 colonne = giorni, righe = orari, dynamic-height
 *   per riga. Card sempre leggibili con ora + nome cognome + coperti.
 * - Step temporale selezionabile 15/30/60 min.
 * - Header (giorni/orari) sticky durante lo scroll.
 * - Click su cella vuota → callback `onCreateAt(date, time)`.
 * - Doppio click su card → callback `onOpenReservation(id)`.
 * - Navigazione indefinita (avanti/indietro/oggi/date-picker) su qualsiasi
 *   data, indipendente da esistenza di prenotazioni.
 * - Eventi calendario (`events` prop) mostrati in banner all-day o inline.
 * - Drag&Drop nativo HTML5 preservato: day → cambia ora; week → cambia
 *   data + ora.
 */
import React, { useMemo, useState, useRef, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CalendarPlus, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { romeToday } from "@/components/agenda/agendaConfig";

const STATUS_STYLES = {
  da_confermare: "bg-amber-100 text-amber-900 border-amber-400",
  confermata:    "bg-emerald-100 text-emerald-900 border-emerald-400",
  arrivato:      "bg-sky-100 text-sky-900 border-sky-400",
  completata:    "bg-neutral-200 text-neutral-800 border-neutral-400",
  annullata:     "bg-rose-100 text-rose-900 border-rose-400 line-through",
  no_show:       "bg-orange-100 text-orange-900 border-orange-400 line-through",
};

// -------- helpers --------------------------------------------------------
const toMinutes = (t) => {
  if (!t || typeof t !== "string") return 0;
  const [h, m] = t.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};
const fromMinutes = (m) => {
  const mm = ((m % 1440) + 1440) % 1440;
  const h = Math.floor(mm / 60);
  const min = mm % 60;
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
};
// Aritmetica su date ISO in UTC: evita lo slittamento di un giorno che
// `new Date(iso+"T00:00:00")` (ora locale) + toISOString() (UTC) produce in Europe/Rome.
const addDays = (isoDate, delta) => {
  const d = new Date(isoDate + "T12:00:00Z");
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
};
const startOfWeek = (isoDate) => {
  const d = new Date(isoDate + "T12:00:00Z");
  const day = (d.getUTCDay() + 6) % 7;  // 0 = lunedì
  d.setUTCDate(d.getUTCDate() - day);
  return d.toISOString().slice(0, 10);
};
const fmtDayLabel = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("it-IT",
  { weekday: "short", day: "2-digit", month: "short" });
const fmtLongDayLabel = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("it-IT",
  { weekday: "long", day: "2-digit", month: "long", year: "numeric" });

const CARD_MIN_HEIGHT = 44;   // min-height each reservation card (dynamic sizing baseline)

// -------- main -----------------------------------------------------------
export default function ReservationCalendar({
  reservations, events, view, selectedDate, onDateChange,
  onUpdated, onOpenReservation, onCreateAt,
  onOpenEvent, onCreateEvent, onEventMove,
}) {
  const isDay = view === "day";
  const gridStartMin = 12 * 60;      // 12:00
  const gridEndMin = 24 * 60;        // 24:00
  const [stepMin, setStepMin] = useState(30);   // 15 | 30 | 60

  // Rows -----------------------------------------------------------------
  const rowsMin = useMemo(() => {
    const out = [];
    for (let m = gridStartMin; m < gridEndMin; m += stepMin) out.push(m);
    return out;
  }, [stepMin]);

  // Week columns ---------------------------------------------------------
  const weekColumns = useMemo(() => {
    const start = startOfWeek(selectedDate);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [selectedDate]);
  const columns = isDay ? [selectedDate] : weekColumns;

  // Bucket reservations by (dateKey, row-slot) --------------------------
  const filteredResv = useMemo(() => {
    if (isDay) return (reservations || []).filter((r) => r.date === selectedDate);
    const set = new Set(weekColumns);
    return (reservations || []).filter((r) => set.has(r.date));
  }, [reservations, isDay, selectedDate, weekColumns]);

  const bucket = useMemo(() => {
    // Map dateKey → rowMin → resv[]
    const b = new Map();
    for (const r of filteredResv) {
      const t = toMinutes(r.time);
      const rowMin = gridStartMin + Math.floor((t - gridStartMin) / stepMin) * stepMin;
      if (rowMin < gridStartMin || rowMin >= gridEndMin) continue;
      if (!b.has(r.date)) b.set(r.date, new Map());
      const rowMap = b.get(r.date);
      if (!rowMap.has(rowMin)) rowMap.set(rowMin, []);
      rowMap.get(rowMin).push(r);
    }
    return b;
  }, [filteredResv, stepMin]);

  // Row-height dinamica: max card count su tutte le colonne per quella riga
  const rowHeights = useMemo(() => {
    const map = new Map();
    for (const rm of rowsMin) {
      let maxN = 0;
      for (const c of columns) {
        const n = bucket.get(c)?.get(rm)?.length || 0;
        if (n > maxN) maxN = n;
      }
      // Base 40px cell height (empty), + N * (CARD_MIN_HEIGHT + 6 gap)
      const h = maxN === 0 ? 40 : Math.max(40, 8 + maxN * (CARD_MIN_HEIGHT + 6));
      map.set(rm, h);
    }
    return map;
  }, [bucket, rowsMin, columns]);

  // Bucket events by date ------------------------------------------------
  const eventsByDate = useMemo(() => {
    const m = new Map();
    for (const e of (events || [])) {
      if (!m.has(e.date)) m.set(e.date, { allDay: [], timed: [] });
      if (e.all_day || !e.time_start) m.get(e.date).allDay.push(e);
      else m.get(e.date).timed.push(e);
    }
    return m;
  }, [events]);

  // Drag & Drop ---------------------------------------------------------
  const draggingIdRef = useRef(null);

  const handleDragStart = (e, r) => {
    draggingIdRef.current = r;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", r.id);
  };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  const applyMove = useCallback(async (r, patch, force = false, previous = null) => {
    try {
      const url = `/admin/reservations/${r.id}` + (force ? "?force=true" : "");
      await api.patch(url, patch);
      const parts = [];
      if (patch.date) parts.push(fmtDayLabel(patch.date));
      if (patch.time) parts.push(patch.time);

      // Undo — computed against the *pre-move* state.
      const prev = previous || { date: r.date, time: r.time };
      toast.success(
        <div className="flex items-center gap-3">
          <span>{r.customer_name} → {parts.join(" · ")}</span>
          <button
            data-testid={`cal-undo-${r.id}`}
            className="text-xs px-2 py-1 rounded bg-white/20 text-white hover:bg-white/30"
            onClick={async () => {
              try {
                await api.patch(`/admin/reservations/${r.id}?force=true`,
                                  { date: prev.date, time: prev.time });
                toast.success("Spostamento annullato");
                onUpdated && onUpdated();
              } catch (e) { toast.error(formatApiError(e)); }
            }}>
            Annulla
          </button>
        </div>,
        { duration: 8000 },
      );
      onUpdated && onUpdated();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.error === "double_booking") {
        toast.error(
          <div>
            Tavolo occupato in questa fascia.
            <div className="mt-1">
              <button
                onClick={() => applyMove(r, patch, true, previous)}
                className="text-xs px-2 py-1 rounded bg-rose-700 text-white"
                data-testid="cal-force-move">
                Forza comunque
              </button>
            </div>
          </div>,
          { duration: 6000 },
        );
      } else {
        toast.error(formatApiError(e));
      }
    }
  }, [onUpdated]);

  const handleDrop = (e, dateKey, rowMin) => {
    e.preventDefault();
    const dragged = draggingIdRef.current;
    draggingIdRef.current = null;
    if (!dragged) return;
    // Event dragged? call the event mover.
    if (dragged.__kind === "event") {
      if (dateKey !== dragged.date && onEventMove) {
        onEventMove(dragged, dateKey);
      }
      return;
    }
    const patch = {};
    if (rowMin !== toMinutes(dragged.time)) patch.time = fromMinutes(rowMin);
    if (dateKey !== dragged.date) patch.date = dateKey;
    if (!Object.keys(patch).length) return;
    applyMove(dragged, patch, false, { date: dragged.date, time: dragged.time });
  };

  // Header navigation ---------------------------------------------------
  const shiftDate = (delta) => {
    onDateChange(addDays(selectedDate, isDay ? delta : delta * 7));
  };
  const jumpToday = () => onDateChange(romeToday());

  // ---------------------------------------------------------------------
  const totalResv = filteredResv.length;

  return (
    <div className="space-y-3" data-testid="resv-calendar">

      {/* Navigation bar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => shiftDate(-1)}
                    data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={jumpToday}
                    data-testid="cal-today">Oggi</Button>
          <Button variant="outline" size="sm" onClick={() => shiftDate(1)}
                    data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
          <Input type="date" value={selectedDate}
                  onChange={(e) => e.target.value && onDateChange(e.target.value)}
                  data-testid="cal-datepicker"
                  className="w-40 h-8 text-xs font-sans tabular-nums" />
        </div>

        <div className="font-sans text-base font-semibold tabular-nums"
              data-testid="cal-label">
          {isDay
            ? fmtLongDayLabel(selectedDate)
            : `${fmtDayLabel(weekColumns[0])} → ${fmtDayLabel(weekColumns[6])}`}
        </div>

        <div className="flex items-center gap-2">
          {/* Step selector */}
          <div className="inline-flex rounded-full border border-sand-300 bg-white p-0.5"
                data-testid="cal-step-switcher">
            {[15, 30, 60].map((s) => (
              <button key={s}
                      onClick={() => setStepMin(s)}
                      data-testid={`cal-step-${s}`}
                      className={`text-xs px-2 py-0.5 rounded-full font-sans tabular-nums
                        ${stepMin === s ? "bg-bronze-600 text-white" : "text-ink-600"}`}>
                {s}′
              </button>
            ))}
          </div>
          <span className="text-xs text-ink-500 font-sans tabular-nums">
            {totalResv} prenot.
          </span>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="rounded-2xl border border-sand-300 bg-white overflow-auto"
            style={{ maxHeight: "calc(100vh - 340px)", minHeight: "500px" }}
            data-testid="cal-scroll">
        <div className="min-w-max">
          {/* All-day events banner (sticky top under header) */}
          {isDay ? (
            <DayAllDayBanner
              date={selectedDate}
              items={eventsByDate.get(selectedDate)?.allDay || []}
              onOpen={onOpenEvent}
              onCreate={onCreateEvent}
              onDragStart={(e, ev) => { draggingIdRef.current = { ...ev, __kind: "event" };
                                          e.dataTransfer.effectAllowed = "move";
                                          e.dataTransfer.setData("text/plain", ev.id); }}
              onDragOver={handleDragOver}
              onDrop={(e, d) => { const dragged = draggingIdRef.current; draggingIdRef.current = null;
                                   if (dragged?.__kind === "event" && d !== dragged.date && onEventMove) onEventMove(dragged, d); }}
              />
          ) : (
            <WeekAllDayBanner
              days={weekColumns}
              eventsByDate={eventsByDate}
              onOpen={onOpenEvent}
              onCreate={onCreateEvent}
              onDragStart={(e, ev) => { draggingIdRef.current = { ...ev, __kind: "event" };
                                          e.dataTransfer.effectAllowed = "move";
                                          e.dataTransfer.setData("text/plain", ev.id); }}
              onDragOver={handleDragOver}
              onDrop={(e, d) => { const dragged = draggingIdRef.current; draggingIdRef.current = null;
                                   if (dragged?.__kind === "event" && d !== dragged.date && onEventMove) onEventMove(dragged, d); }}
              />
          )}

          {/* Sticky header row */}
          <div className="grid sticky top-0 bg-sand-100 border-b border-sand-300 z-20"
                style={{
                  gridTemplateColumns: `70px repeat(${columns.length}, minmax(${isDay ? 480 : 160}px, 1fr))`,
                }}>
            <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-ink-500 font-sans">Ora</div>
            {columns.map((c) => (
              <div key={c}
                    className="px-2 py-1.5 text-xs font-semibold text-ink-700 border-l border-sand-200 font-sans tabular-nums"
                    data-testid={`cal-col-${c}`}>
                {isDay ? "Prenotazioni" : fmtDayLabel(c)}
              </div>
            ))}
          </div>

          {/* Body rows */}
          {rowsMin.map((rowMin) => {
            const h = rowHeights.get(rowMin);
            return (
            <div key={rowMin}
                  className="grid border-t border-sand-100"
                  style={{
                    gridTemplateColumns: `70px repeat(${columns.length}, minmax(${isDay ? 480 : 160}px, 1fr))`,
                    minHeight: `${h}px`,
                  }}>
              {/* Sticky time gutter */}
              <div className="px-2 py-1 text-[11px] text-ink-500 bg-sand-50 sticky left-0 z-10
                              font-sans tabular-nums border-r border-sand-200">
                {fromMinutes(rowMin)}
              </div>

              {columns.map((dateKey) => {
                const items = bucket.get(dateKey)?.get(rowMin) || [];
                const timedEvents = isDay
                  ? (eventsByDate.get(dateKey)?.timed || []).filter((ev) => {
                      const t = toMinutes(ev.time_start);
                      return t >= rowMin && t < rowMin + stepMin;
                    })
                  : [];
                return (
                  <div key={dateKey}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, dateKey, rowMin)}
                        onDoubleClick={() => onCreateAt && onCreateAt(dateKey, fromMinutes(rowMin))}
                        className="border-l border-sand-200 p-1 space-y-1
                                    hover:bg-sand-50/60 transition-colors cursor-copy"
                        data-testid={`cal-cell-${dateKey}-${fromMinutes(rowMin)}`}>
                    {/* Timed events (small badges) */}
                    {timedEvents.map((ev) => (
                      <div key={ev.id}
                            className="text-[11px] rounded px-1.5 py-0.5 flex items-center gap-1"
                            style={{
                              borderLeft: `3px solid ${ev.color}`,
                              background: `${ev.color}18`,
                              color: ev.color,
                            }}
                            data-testid={`cal-event-${ev.id}`}>
                        <Sparkles className="w-3 h-3" />
                        <b>{ev.time_start}</b> {ev.title}
                      </div>
                    ))}
                    {/* Reservations */}
                    {items.map((r) => (
                      <ResvCard key={r.id} r={r}
                                  onDragStart={(e) => handleDragStart(e, r)}
                                  onOpen={() => onOpenReservation && onOpenReservation(r.id)}
                                  compact={!isDay} />
                    ))}
                  </div>
                );
              })}
            </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="text-[11px] text-ink-400 flex items-center gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1">
          <CalendarPlus className="w-3.5 h-3.5" /> Doppio-click su cella vuota per creare
        </span>
        <span>· Trascina card per spostare</span>
        <span>· Doppio-click card per modificare</span>
      </div>
    </div>
  );
}


// -------- Reservation card (single unit) -------------------------------
function ResvCard({ r, onDragStart, onOpen, compact }) {
  return (
    <button draggable
              onDragStart={onDragStart}
              onDoubleClick={onOpen}
              className={`w-full text-left rounded-md border font-sans px-2 py-1.5
                          cursor-grab active:cursor-grabbing hover:brightness-95
                          ${STATUS_STYLES[r.status] || ""}`}
              style={{ minHeight: CARD_MIN_HEIGHT }}
              data-testid={`cal-item-${r.id}`}>
      {/* Row 1: time + pax + optional table */}
      <div className="flex items-center gap-2 text-[11px] tabular-nums font-semibold">
        <span>{r.time}</span>
        <span>·</span>
        <span>{r.pax}p</span>
        {r.table_number && (
          <span className="ml-auto text-[10px] px-1 rounded border border-current/40 opacity-80">
            {r.table_number}
          </span>
        )}
      </div>
      {/* Row 2: name + surname (always visible) */}
      <div className={`font-semibold ${compact ? "text-xs leading-tight" : "text-sm"} truncate`}>
        {r.customer_name} {r.customer_surname || ""}
      </div>
      {/* Row 3: zone (only if visible without cramming) */}
      {!compact && r.zone && (
        <div className="text-[10px] opacity-70 tabular-nums">{r.zone}</div>
      )}
    </button>
  );
}


// -------- All-day event banners -----------------------------------------
function DayAllDayBanner({ date, items, onOpen, onCreate, onDragStart, onDragOver, onDrop }) {
  return (
    <div className="sticky top-0 z-30 bg-white border-b border-sand-200 px-3 py-2 flex flex-wrap gap-1.5 items-center"
          onDragOver={onDragOver}
          onDrop={(e) => onDrop && onDrop(e, date)}
          data-testid="cal-events-allday">
      {(items || []).map((ev) => (
        <button key={ev.id}
              draggable
              onDragStart={(e) => onDragStart && onDragStart(e, ev)}
              onClick={() => onOpen && onOpen(ev)}
              className="inline-flex items-center gap-1 text-xs font-sans px-2 py-1 rounded-full border cursor-pointer hover:brightness-95"
              style={{ borderColor: ev.color, background: `${ev.color}14`, color: ev.color }}
              title={ev.notes || ev.title}
              data-testid={`cal-event-${ev.id}`}>
          <Sparkles className="w-3 h-3" /> {ev.title}
        </button>
      ))}
      <button onClick={() => onCreate && onCreate(date)}
                className="text-xs text-ink-500 hover:text-bronze-700 underline underline-offset-2 ml-1"
                data-testid="cal-event-add">
        + evento
      </button>
    </div>
  );
}

function WeekAllDayBanner({ days, eventsByDate, onOpen, onCreate, onDragStart, onDragOver, onDrop }) {
  return (
    <div className="grid bg-white border-b border-sand-200"
          style={{ gridTemplateColumns: `70px repeat(${days.length}, minmax(160px, 1fr))` }}
          data-testid="cal-events-allday-week">
      <div className="px-2 py-1 text-[9px] uppercase text-ink-400 font-sans">Eventi</div>
      {days.map((d) => (
        <div key={d}
              className="px-1.5 py-1 border-l border-sand-100 space-y-1 min-h-[28px]"
              onDragOver={onDragOver}
              onDrop={(e) => onDrop && onDrop(e, d)}>
          {(eventsByDate.get(d)?.allDay || []).map((ev) => (
            <button key={ev.id}
                  draggable
                  onDragStart={(e) => onDragStart && onDragStart(e, ev)}
                  onClick={() => onOpen && onOpen(ev)}
                  className="w-full text-left text-[10px] rounded px-1.5 py-0.5 font-sans truncate cursor-pointer hover:brightness-95"
                  style={{ borderLeft: `3px solid ${ev.color}`, background: `${ev.color}14`, color: ev.color }}
                  title={ev.title}
                  data-testid={`cal-event-${ev.id}`}>
              {ev.title}
            </button>
          ))}
          <button onClick={() => onCreate && onCreate(d)}
                    className="text-[10px] text-ink-400 hover:text-bronze-700"
                    data-testid={`cal-event-add-${d}`}>
            + evento
          </button>
        </div>
      ))}
    </div>
  );
}
